import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// TODO: aggiorna path al tuo DTO reale
import { LoadingLists } from '../interfaces/LoadingLists.interfaces';
import { LoadingListStatus } from '../pages/loading-list/loading-list.component';
import { ClientsBrands } from './routing.service';

export interface LoadingListSearchRequest {
  warehouseCode?: string | null;   // singolo magazzino (come il tuo filtro)
  status?: string | null;
  dateStart?: string | null;   // ISO string
  dateEnd?: string | null;     // ISO string
  clients: ClientsBrands;
  loadingListCode?: string | null;
}

export interface SearchWarehouseRequest {
  warehouseCode: string;
}

export interface TourGroupsResponse {
  tourRoutingGroupId: number;
  tourRoutingGroupName: string;
}

export interface CarrierRequest {
  tourRoutingGroupId: number[];
  extra?: boolean;
}

export interface CarrierResponse {
  carrierCode: string;
  carrierName: string;
  defaultCarrier: boolean;
}

export interface CarrierListResponse {
  carriers: CarrierResponse[];
}

export type LoadingListType = 'ARRIVAL' | 'DEPARTURE';

export interface TourGroupsRequest {
  tourRoutingGroupId: number;
  tourRoutingGroupName: string;
}

export interface LoadingListCarrierRequest {
  carrierCode: string;
  carrierName: string;
}

export interface LoadingListManagementRequest {
  warehouseCode: string;
  selectedTours: TourGroupsRequest[];
  selectedCarrier: LoadingListCarrierRequest;
  extra: boolean;
  loadingListPlateNo: string;
  loadingListType: LoadingListType;
  items?: LoadingListItemRequest[];
}

export interface LoadingListRequest {
  warehouseCode: string;
  selectedTours: TourGroupsRequest[];
  selectedCarrier: LoadingListCarrierRequest;
  extra: boolean;
  loadingListPlateNo: string;
  loadingListType: LoadingListType;
  items?: LoadingListItemRequest[];
  clients: ClientsBrands[];
}

export interface LoadingListResponse {
  loadingListId: number;
  loadingListCode?: string;
}

export interface LoadingListItemRequest {
  loadingListItemParcelComplementaryId: number;
  loadingListItemParcelId: number;
  loadingListItemStatus: LoadingListItemStatus;
  loadingListItemCreatedBy: string;
  loadingListItemBarcode: string;
}

export type LoadingListItemStatus = 'PENDING' | 'LOADED' | 'UNLOADED' | 'NOT_ARRIVED' | 'ARRIVED' | 'NOT_LOADED';

export interface LoadingListItemsResponse {
  loadingListItemId: number | null;
  loadingListId: number | null;
  barcode: string;

  loadingListItemParcelComplementaryId: number;
  loadingListItemParcelId: number;

  loadedDate?: string | null;
  unloadedDate?: string | null;
  arrivedDate?: string | null;

  loadingListItemStatus: LoadingListItemStatus;

  tourRoutingGroupName: string;

  parcelCode: string;
  weightKg: number | string | null;
  volumeM3: number | string | null;
  parcelDate?: string;
  parcelEstimatedDeliveryDate?: string;
  parcelPalletType?: string;
  regionAcronym?: string;
  urgencyCode?: string;
  clientAcronym?: string;

  parcelInactiveForLoadingLists?: boolean;
  parcelWithoutManifest?: boolean;
}

export interface LoadingListItemsPreviewResponse {
  totalCount: number;
  loadingListItems: LoadingListItemsResponse[];
}

export interface LoadingListItemsRequest {
  loadingListId: number,
  loadingListType: LoadingListType,
  unloadingClosure: boolean,
  loadingListStatus: string,
  loadingListItems: LoadingListItemRequest[];
  clients: ClientsBrands[];
}


@Injectable({ providedIn: 'root' })
export class LoadingListService {

  private readonly baseUrl = `${environment.apiUrl}/loading-list`;
  
  constructor(private http: HttpClient) {}


  searchLoadingLists(req: LoadingListSearchRequest): Observable<LoadingLists[]> {
    return this.http.post<LoadingLists[]>(`${this.baseUrl}/all`, req);
  }

  printLoadingLists(loadingListIds: number[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/print`, { loadingListIds }, { responseType: 'blob' });
  }

  exportLoadingLists(loadingListIds: number[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/export`, { loadingListIds }, { responseType: 'blob' });
  }


  getById(loadingListId: number): Observable<LoadingLists> {
    return this.http.get<LoadingLists>(`${this.baseUrl}/${loadingListId}`);
  }

  searchTourGroups(req: SearchWarehouseRequest): Observable<TourGroupsResponse[]> {
    return this.http.post<TourGroupsResponse[]>(`${this.baseUrl}/searchTourGroups`, req);
  }

  searchTourGroupCarriers(req: CarrierRequest): Observable<CarrierListResponse> {
    return this.http.post<CarrierListResponse>(`${this.baseUrl}/searchTourGroupCarriers`, req);
  }

  createLoadingList(req: LoadingListRequest): Observable<LoadingListResponse> {
    return this.http.post<LoadingListResponse>(`${this.baseUrl}/createLoadingList`, req);
  }

  getValidLoadingListItems(req: LoadingListManagementRequest): Observable<LoadingListItemsPreviewResponse> {
    return this.http.post<LoadingListItemsPreviewResponse>(`${this.baseUrl}/getValidLoadingListItems`, req);
  }

  getValidLoadingListItemsToInvalidate(req: LoadingListManagementRequest): Observable<LoadingListItemsPreviewResponse> {
    return this.http.post<LoadingListItemsPreviewResponse>(`${this.baseUrl}/getValidLoadingListItemsToInvalidate`, req);
  }

  // getLoadingListItems(loadingListId: number): Observable<LoadingListItemsResponse[]> {
  //   return this.http.get<LoadingListItemsResponse[]>(`${this.baseUrl}/${loadingListId}/getItems`);
  // }

  getLoadingListItems(
    loadingListId: number,
    warehouseCode: string,
    loadingListType: string
  ): Observable<LoadingListItemsResponse[]> {
    return this.http.get<LoadingListItemsResponse[]>(
      `${this.baseUrl}/${loadingListId}/${warehouseCode}/${loadingListType}/getItems`
    );
  }

  updateBordero(req: LoadingListItemsRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/updateLoadingListItems`, req);
  }

  getPossibleWarehouses(loadingListId: number): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.baseUrl}/${loadingListId}/possibleWarehouses`
    );
  }

  deleteLoadingList(loadingListId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${loadingListId}/deleteLoadingList`, {});
  }

  invalidateParcelsForLoadingLists(parcelCodes: string[], isToInvalid: boolean) {
    return this.http.post<any>(
      `${this.baseUrl}/invalidate-parcels-for-loading-lists`,
      {
        parcelCodes,
        isToInvalid
      }
    );
  }

  printDDTFromLoadingLists(loadingListIds: number[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/printDDT`, { loadingListIds }, { responseType: 'blob' });
  }

    downloadLinkedTransportDocuments(loadingListIds: number[], fallbackFileName: string): Observable<string> {
    return this.http.post(
      `${environment.apiUrl}/loading-list/printLinkedTransportDocuments`,
      { loadingListIds },
      { observe: 'response', responseType: 'blob' }
    ).pipe(
      map((res: HttpResponse<Blob>) => {
        const fileName = this.extractFileName(res) ?? fallbackFileName;
        const url = window.URL.createObjectURL(res.body as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        return fileName;
      })
    );
  }
 
  private extractFileName(res: HttpResponse<Blob>): string | null {
    const contentDisposition = res.headers.get('Content-Disposition');
    const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  getLoadingListTraction(loadingListId: number): Observable<{
    tripPointCarrierCode: string | null;
    tripPointTractorPlateNo: string | null;
    tripPointDriverName: string | null;
  }> {
    return this.http.get<any>(`${this.baseUrl}/${loadingListId}/traction`);
  }
}
